import React from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, ArrowRight, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PRICING, FAQ } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOOKUP_KEYS = {
  Crew: { monthly: 'crewface_crew_monthly', yearly: 'crewface_crew_yearly' },
  Contractor: { monthly: 'crewface_contractor_monthly', yearly: 'crewface_contractor_yearly' },
  Site: { monthly: 'crewface_site_monthly', yearly: 'crewface_site_yearly' },
  Fleet: null
};

const YEARLY_PRICE = { Crew: 490, Contractor: 990, Site: 1990 };

const savingsFor = (planName, monthlyPrice) => {
  if (monthlyPrice == null || !YEARLY_PRICE[planName]) return 0;
  return monthlyPrice * 12 - YEARLY_PRICE[planName];
};

const maxSavings = Math.max(...PRICING.map((p) => savingsFor(p.name, p.price)));

export default function Pricing() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(null);
  const [yearly, setYearly] = React.useState(false);

  const [fleetOpen, setFleetOpen] = React.useState(false);
  const [leadName, setLeadName] = React.useState(user?.name || '');
  const [leadEmail, setLeadEmail] = React.useState(user?.email || '');
  const [leadCompany, setLeadCompany] = React.useState(user?.company || '');
  const [leadSeats, setLeadSeats] = React.useState('');
  const [leadMessage, setLeadMessage] = React.useState('');
  const [leadSubmitting, setLeadSubmitting] = React.useState(false);
  const [leadSubmitted, setLeadSubmitted] = React.useState(false);

  const checkout = async (plan) => {
    const keys = LOOKUP_KEYS[plan.name];
    if (!keys) {
      setFleetOpen(true);
      return;
    }
    const lookup = yearly ? keys.yearly : keys.monthly;
    setLoading(plan.name);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookup,
        origin_url: window.location.origin,
        user_id: user?.user_id || null,
        user_email: user?.email || null
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      console.error(e);
      toast({ title: 'Could not start checkout', description: e?.response?.data?.detail || 'Please try again.' });
      setLoading(null);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: leadName, email: leadEmail, company: leadCompany,
        seats_needed: leadSeats, message: leadMessage, plan: 'fleet',
      });
      setLeadSubmitted(true);
    } catch (e) {
      toast({ title: 'Could not send request', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLeadSubmitting(false);
    }
  };

  const closeFleetDialog = () => {
    setFleetOpen(false);
    setLeadSubmitted(false);
    setLeadSeats(''); setLeadMessage('');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c]">
      <Navbar />
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-10 text-center">
        <Badge className="bg-[#14141a] border border-[#26262e] text-[#c9c6bf] hover:bg-[#14141a] rounded-full mb-5">Pricing</Badge>
        <h1 className="serif text-5xl md:text-6xl text-white leading-tight">Priced per crew.<br /><span className="italic text-[#ea9a6f]">Not per selfie.</span></h1>
        <p className="text-[#a8a49b] mt-5 max-w-xl mx-auto">Simple per-seat plans. Free trial with 2 headshots. Fleet-level SSO, SOC 2, and custom retention available.</p>
        {!user && <p className="text-xs text-[#7a7770] mt-3">Test card: <span className="mono text-[#c9c6bf]">4242 4242 4242 4242</span> · any future date · any CVC</p>}

        <div className="mt-8 inline-flex items-center gap-1 bg-[#101014] border border-[#1e1e26] rounded-full p-1" data-testid="billing-toggle">
          <button
            onClick={() => setYearly(false)}
            data-testid="billing-monthly-button"
            className={`text-sm px-5 py-2 rounded-full transition-colors ${!yearly ? 'bg-[#ea9a6f] text-[#0a0a0c]' : 'text-[#c9c6bf] hover:text-white'}`}
          >Monthly</button>
          <button
            onClick={() => setYearly(true)}
            data-testid="billing-yearly-button"
            className={`text-sm px-5 py-2 rounded-full transition-colors flex items-center gap-1.5 ${yearly ? 'bg-[#ea9a6f] text-[#0a0a0c]' : 'text-[#c9c6bf] hover:text-white'}`}
          >Yearly <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${yearly ? 'bg-[#0a0a0c] text-[#7dd4c5]' : 'bg-[#7dd4c5]/15 text-[#7dd4c5]'}`}>2 months free</span></button>
        </div>
        <p className="text-xs text-[#7dd4c5] mt-3" data-testid="max-savings-callout">
          {yearly ? `You're saving up to $${maxSavings}/year with annual billing` : `Switch to yearly and save up to $${maxSavings}/year`}
        </p>
      </section>

      {/* Why monthly, not one-time */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 pb-14">
        <div className="bg-[#101014] border border-[#1e1e26] rounded-2xl p-6 md:p-7 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#1a1a20] border border-[#26262e] flex items-center justify-center shrink-0">
            <RefreshCw className="w-5 h-5 text-[#ea9a6f]" />
          </div>
          <div>
            <div className="text-white text-sm font-semibold mb-1">Why a subscription, not a one-time fee?</div>
            <p className="text-sm text-[#a8a49b] leading-relaxed">Crews change constantly — new hires, replacement badges, seasonal workers. CrewFace keeps every worker's ID photo current automatically, so you're never paying a photographer or re-running a batch job for one new hire.</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {PRICING.map((p) => {
          const isYearly = yearly && YEARLY_PRICE[p.name];
          const displayPrice = isYearly ? YEARLY_PRICE[p.name] : p.price;
          const displayUnit = isYearly ? '/yr' : p.unit;
          const savings = savingsFor(p.name, p.price);
          return (
            <div key={p.name} className={`relative rounded-3xl p-8 border ${p.highlighted ? 'border-[#ea9a6f] bg-[#141210]' : 'border-[#1e1e26] bg-[#101014]'}`}>
              {p.highlighted && <div className="absolute -top-3 left-8 bg-[#ea9a6f] text-[#0a0a0c] text-xs font-semibold px-3 py-1 rounded-full">Most popular</div>}
              <div className="serif text-3xl text-white">{p.name}</div>
              <p className="text-sm text-[#8a877f] mt-1">{p.tagline}</p>
              <div className="mt-6 mb-6">
                {displayPrice !== null ? (
                  <>
                    <div className="flex items-baseline gap-1">
                      <span className="serif text-5xl text-white" data-testid={`price-${p.name}`}>${displayPrice}</span>
                      <span className="text-[#7a7770]">{displayUnit}</span>
                    </div>
                    {isYearly && savings > 0 && (
                      <div className="text-xs text-[#7dd4c5] mt-1.5 flex items-center gap-1" data-testid={`savings-${p.name}`}>
                        <Sparkles className="w-3 h-3" /> Save ${savings}/yr vs. monthly
                      </div>
                    )}
                  </>
                ) : (
                  <div className="serif text-4xl text-white">Custom</div>
                )}
              </div>
              <Button
                onClick={() => (p.price !== null ? (user ? checkout(p) : navigate('/sign-up')) : setFleetOpen(true))}
                disabled={loading === p.name}
                data-testid={`checkout-${p.name}-button`}
                className={`w-full rounded-full h-11 ${p.highlighted ? 'bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c]' : 'bg-[#1a1a20] hover:bg-[#22222a] text-white border border-[#26262e]'}`}
              >
                {loading === p.name ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting checkout…</>) : (<>{user ? (p.price !== null ? `Subscribe to ${p.name}` : p.cta) : p.cta} <ArrowRight className="ml-1.5 w-4 h-4" /></>)}
              </Button>
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#c9c6bf]">
                    <Check className="w-4 h-4 text-[#7dd4c5] shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </section>

      <section className="max-w-4xl mx-auto px-5 md:px-8 py-10 text-center text-xs text-[#7a7770]">
        Sandbox mode — all payments are test payments. Tax handling: Stripe manages full tax calculation, filing and remittance (Managed Payments, +3.5% per transaction).
      </section>

      <section className="max-w-4xl mx-auto px-5 md:px-8 py-14">
        <h2 className="serif text-4xl md:text-5xl text-white text-center mb-10">Common questions</h2>
        <Accordion type="single" collapsible className="space-y-2">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`i${i}`} className="bg-[#101014] border border-[#1e1e26] rounded-xl px-5">
              <AccordionTrigger className="text-white text-left hover:no-underline py-4">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[#a8a49b] pb-4">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
      <Footer />

      <Dialog open={fleetOpen} onOpenChange={(o) => (o ? setFleetOpen(true) : closeFleetDialog())}>
        <DialogContent className="bg-[#101014] border-[#26262e] text-white">
          <DialogHeader>
            <DialogTitle className="serif text-2xl">Talk to sales about Fleet</DialogTitle>
          </DialogHeader>
          {!leadSubmitted ? (
            <form onSubmit={submitLead} className="space-y-3">
              <div>
                <Label className="text-[#c9c6bf] text-sm">Your name</Label>
                <Input required value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Sam Rivera" data-testid="fleet-name-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
              </div>
              <div>
                <Label className="text-[#c9c6bf] text-sm">Work email</Label>
                <Input type="email" required value={leadEmail} onChange={(e) => setLeadEmail(e.target.value)} placeholder="sam@company.com" data-testid="fleet-email-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
              </div>
              <div>
                <Label className="text-[#c9c6bf] text-sm">Company</Label>
                <Input value={leadCompany} onChange={(e) => setLeadCompany(e.target.value)} placeholder="Acme Fleet Services" data-testid="fleet-company-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
              </div>
              <div>
                <Label className="text-[#c9c6bf] text-sm">Seats needed</Label>
                <Input value={leadSeats} onChange={(e) => setLeadSeats(e.target.value)} placeholder="e.g. 500+" data-testid="fleet-seats-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white h-11" />
              </div>
              <div>
                <Label className="text-[#c9c6bf] text-sm">Anything else?</Label>
                <Textarea value={leadMessage} onChange={(e) => setLeadMessage(e.target.value)} placeholder="Multi-site rollout, HR system, timeline…" data-testid="fleet-message-input" className="mt-1.5 bg-[#0a0a0c] border-[#26262e] text-white" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={leadSubmitting} data-testid="fleet-submit-button" className="bg-[#ea9a6f] hover:bg-[#e0895a] text-[#0a0a0c] rounded-full">
                  {leadSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : 'Send to sales'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="text-center py-4" data-testid="fleet-success-panel">
              <div className="w-12 h-12 rounded-full bg-[#7dd4c5]/15 flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-[#7dd4c5]" />
              </div>
              <h3 className="serif text-2xl text-white mb-1">Thanks, {leadName.split(' ')[0]}!</h3>
              <p className="text-sm text-[#8a877f]">Our sales team will reach out within one business day.</p>
              <Button onClick={closeFleetDialog} data-testid="fleet-done-button" variant="outline" className="mt-6 border-[#2a2a30] bg-transparent text-white hover:bg-[#14141a] rounded-full">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

